import * as React from 'react';

import { QuestionnaireDesignerProps } from "./questionnaire-designer.types";
import { QuestionnairePageType } from '@common/dto/questionnaire/page/type';
import { generateId } from '@common/utils/common';
import { QuestionnairePageCreateLocalDto } from '@modules/fhq-design-tool/dto/page-create-local-dto';
import { QuestionnairePageDeleteDto } from '@common/dto/questionnaire/page/delete-dto';
import { QuestionnairePageListDto } from '@common/dto/questionnaire/page/list-dto';
import { QuestionnaireUpdateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-update-local-dto';
import { QuestionnaireFinalNotesPageCreateLocalDto } from '@modules/fhq-design-tool/dto/final-notes-page-create-local-dto';
import { QuestionnairePageUpdateDto } from '@common/dto/questionnaire/page/update-dto';
import { QuestionnaireCreateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-create-local-dto';
import { QuestionnairePageGetDto } from '@common/dto/questionnaire/page/get-dto';

export type QuestionnaireVisiblePage = 
    QuestionnairePageCreateLocalDto | 
    QuestionnairePageListDto |
    QuestionnaireFinalNotesPageCreateLocalDto;

export const useQuestionnaireDesignerController = (props: QuestionnaireDesignerProps) => {
  const { onChange, questionnaire, isPageLoaded, markPageLoaded, getPage, lastSaveQuestionnaireIndicator, errors, onExistingPageLoadAndAddToQuestionnaire } = props;
  const [customPagesIncrement, setCustomPagesIncrement] = React.useState(0);
  const [currentEditingPageId, setCurrentEditingPageId] = React.useState<string | number | undefined>();

  const currentEditingPage = questionnaire.designerTab.pages.find((x) => x.id === currentEditingPageId) as QuestionnaireVisiblePage | undefined;

  React.useEffect(() => {
    const existingPageIsBeingEdited = currentEditingPage && !(currentEditingPage as QuestionnairePageCreateLocalDto).isNotCreated;

    if (existingPageIsBeingEdited) {
      handleLoadPage(currentEditingPage.id as number);
    } else {
      setCurrentEditingPageId(undefined);
    }
  }, [lastSaveQuestionnaireIndicator]);

  React.useEffect(() => {
    setCustomPagesIncrement(0);
  }, [questionnaire.questionnaireId]);

  const getDeletedPages = React.useCallback((questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => {
    return questionnaire.designerTab.pages.filter((x) => (x as QuestionnairePageDeleteDto).deleted);
  }, []);
  
  const handlePagesOrderChange = React.useCallback((newPagesOrder: Array<QuestionnaireVisiblePage>) => {
    const { designerTab: { pages, ...otherDesignerTab }, ...restQuestionnaire } = questionnaire;
    
    const deletedPages = getDeletedPages(questionnaire);

    onChange({
      ...restQuestionnaire,
      designerTab: {
        pages: [
          ...newPagesOrder.map((x, i) => ({
            ...x,
            modified: true,
            order: i + 1,
          })),
          ...deletedPages,
        ],
        ...otherDesignerTab,
      },
    });
  }, [onChange, questionnaire, getDeletedPages]);

  const handlePageAdd = () => {
    const newOrder = customPagesIncrement + 1;
    const { designerTab: { pages, ...otherDesignerTab }, ...restQuestionnaire } = questionnaire;
    const copyPages = pages.slice();

    copyPages.unshift({
      id: generateId(),
      isNotCreated: true,
      skipLogicEnabled: false,
      enabled: true,
      order: 0,
      fields: [],
      title: `Page ${newOrder}`,
      pageType: QuestionnairePageType.normal,
      modified: true,
    });

    onChange({
      ...restQuestionnaire,
      designerTab: {
        pages: copyPages
          .sort((a, b) => 
            (a as QuestionnaireVisiblePage).order - (b as QuestionnaireVisiblePage).order
          )
          .map((x, i) => ({
            ...x,
            modified: (x as QuestionnairePageDeleteDto).deleted ? false : true,
            order: i + 1,
          })),
        ...otherDesignerTab,
      },
    });
    setCustomPagesIncrement(newOrder);
  };

  const handlePageDelete = React.useCallback((page: QuestionnaireVisiblePage) => {
    const { designerTab: { pages, ...otherDesignerTab }, ...restQuestionnaire } = questionnaire;
    
    onChange({
      ...restQuestionnaire,
      designerTab: {
        pages: pages
          .sort((a, b) => 
            (a as QuestionnaireVisiblePage).order - (b as QuestionnaireVisiblePage).order
          )
          .map((p, i) => ({
            ...p,
            modified: page.id === p.id ? false : (p as QuestionnairePageUpdateDto).modified,
            deleted: page.id === p.id ? true : (p as QuestionnairePageDeleteDto).deleted,
            order: i + 1,
          })),
        ...otherDesignerTab,
      },
    });
  }, [onChange, questionnaire]);

  const navigateOnPageEdit = React.useCallback((page: QuestionnaireVisiblePage) => {
    setCurrentEditingPageId(page.id);
  }, []);

  const resetSelectedPage = React.useCallback(() => {
    setCurrentEditingPageId(undefined);
  }, []);

  const canDeletePage = React.useCallback((page: QuestionnaireVisiblePage) => {
    return page.pageType === QuestionnairePageType.normal;
  }, []);

  const canShowHidePage = React.useCallback((page: QuestionnaireVisiblePage) => {
    return page.pageType !== QuestionnairePageType.normal;
  }, []);

  const canChangePageOrder = React.useCallback((page: QuestionnaireVisiblePage) => {
    return page.pageType === QuestionnairePageType.normal;
  }, []);

  const handlePageChange = React.useCallback((page: QuestionnaireVisiblePage) => {
    const { designerTab: { pages, ...otherDesignerTab }, ...restQuestionnaire } = questionnaire;
   
    onChange({
      ...restQuestionnaire,
      designerTab: {
        pages: pages.map((x) => x.id === page.id ? page : x),
        ...otherDesignerTab,
      },
    });
  }, [onChange, questionnaire]);

  const handlePageShowStatusChange = React.useCallback((page: QuestionnaireVisiblePage, isVisible: boolean) => {
    handlePageChange({
      ...page,
      modified: true,
      enabled: isVisible,
    });
  }, [handlePageChange]);

  const handleLoadPage = React.useCallback(async (pageId: QuestionnairePageListDto['id']) => {
    if (!getPage) {
      return;
    }

    const page = await getPage(pageId);

    const { designerTab: { pages, ...otherDesignerTab }, ...restQuestionnaire } = questionnaire;

    onExistingPageLoadAndAddToQuestionnaire({
      ...restQuestionnaire,
      designerTab: {
        pages: pages.map((x) => {
          if (x.id === page.id) {
            (page as QuestionnairePageGetDto).enabled = (x as QuestionnairePageListDto).enabled;

            return page;
          }

          return x;
        }) as Array<QuestionnaireVisiblePage>,
        ...otherDesignerTab,
      },
    });
    markPageLoaded(page.id);

    return page;
  }, [onChange, questionnaire, markPageLoaded]);

  const sortedPages = questionnaire.designerTab.pages
    .filter((x) => !(x as QuestionnairePageDeleteDto).deleted)
    .sort((a, b) => 
      (a as QuestionnaireVisiblePage).order - (b as QuestionnaireVisiblePage).order
    ) as Array<QuestionnaireVisiblePage>;
  
  const isCurrentEditingPageLoadedAlready = 
    currentEditingPage && 
    (
      (currentEditingPage as QuestionnairePageCreateLocalDto).isNotCreated || 
      isPageLoaded((currentEditingPage as QuestionnairePageUpdateDto | QuestionnairePageListDto).id)
    );

  const currentEditingPageErrors = React.useMemo(() => (errors?.pages && currentEditingPage) ? errors.pages[currentEditingPage.id] : undefined, [errors?.pages, currentEditingPage?.id])

  return {
    isCurrentEditingPageLoadedAlready,
    sortedPages,
    currentEditingPage,
    handleLoadPage,
    handlePageChange,
    resetSelectedPage,
    canChangePageOrder,
    canShowHidePage,
    canDeletePage,
    handlePagesOrderChange,
    handlePageAdd,
    handlePageDelete,
    navigateOnPageEdit,
    handlePageShowStatusChange,
    currentEditingPageErrors
  };
};